/**
 * Referral Link Hook
 *
 * Handles deep linking for referral codes.
 * Automatically detects and applies referral codes from URLs.
 */

import { useEffect } from 'react';
import { useLocalSearchParams } from 'expo-router';
import * as Linking from 'expo-linking';

import { useReferralStore } from './referral-store';

// ============================================================================
// Constants
// ============================================================================

export const REFERRAL_PARAM = 'ref';
export const APP_BASE_URL = 'https://inverio.ai';

// ============================================================================
// Hook: Auto-detect referral from URL
// ============================================================================

/**
 * Hook to automatically detect and store referral code from URL params.
 * Call this in your root layout or signup screen.
 */
export function useReferralFromUrl() {
  const params = useLocalSearchParams<{ ref?: string }>();
  const appliedCode = useReferralStore((s) => s.appliedCode);
  const setPendingCode = useReferralStore((s) => s.setPendingCode);
  const pendingCode = useReferralStore((s) => s.pendingCode);

  useEffect(() => {
    const refCode = params.ref;

    // If there's a ref param and we haven't already applied a code
    if (refCode && !appliedCode) {
      // Store as pending - will be applied during subscription
      setPendingCode(refCode.toUpperCase());
    }
  }, [params.ref, appliedCode, setPendingCode]);

  return {
    pendingCode,
    hasPendingReferral: !!pendingCode && !appliedCode,
  };
}

// ============================================================================
// Helper: Generate shareable referral link
// ============================================================================

/**
 * Generate a shareable referral link with the code embedded
 */
export function generateReferralLink(code: string, path: string = '/signup'): string {
  return `${APP_BASE_URL}${path}?${REFERRAL_PARAM}=${code}`;
}

/**
 * Generate deep link for the app (expo-linking format)
 */
export function generateDeepLink(code: string, path: string = '/signup'): string {
  return Linking.createURL(path, {
    queryParams: { [REFERRAL_PARAM]: code },
  });
}

// ============================================================================
// Helper: Extract referral code from URL
// ============================================================================

/**
 * Extract referral code from any URL
 */
export function extractReferralCode(url: string): string | null {
  try {
    const parsed = Linking.parse(url);
    const refCode = parsed.queryParams?.[REFERRAL_PARAM];

    if (typeof refCode === 'string' && refCode.length > 0) {
      return refCode.toUpperCase();
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Check if a referral code format is valid
 */
export function isValidReferralCodeFormat(code: string): boolean {
  // Format: INV-XXXXXX or FND-XXXXXX
  const codeRegex = /^(INV|FND)-[A-Z0-9]{6}$/;
  return codeRegex.test(code.toUpperCase());
}
